<template>
  <BlBackdrop title="Invite new bulk members from CSV" :footer="false">
    <div class="flex flex-col gap-4 pb-8 px-8" :style="{ width: '40rem' }">
      <div class="grid grid-cols-2">
        <div
          v-for="progress in invitationProgress"
          :key="progress.step"
          :class="
            progress.step <= currentStep
              ? 'border-light-purple'
              : 'border-light-purple-100'
          "
          class="flex flex-col gap-1 pb-1 border-b-4"
        >
          <BlTextBody3 :text="`Step ${progress.step}:`" />
          <BlTextSubtitle2
            :class="{ 'text-grey-400': progress.step > currentStep }"
            :text="progress.label"
          />
        </div>
      </div>

      <div v-if="currentStep === 1" class="flex flex-col gap-4">
        <div class="flex flex-col gap-1">
          <BlTextSubtitle3 text="Download CSV template" />
          <BlTextBody2
            text="Before upload the file, you can use this template:"
          />
          <a
            href="/static/template-bluk-invite-member.csv"
            download="template-bluk-invite-member.csv"
          >
            <BlInputButton type="outlined" label="Download template" />
          </a>
        </div>
        <div class="flex flex-col gap-1">
          <input
            type="file"
            name="upload_csv_member"
            id="upload_csv_member"
            hidden
            accept=".csv"
            @change="onButtonUpload"
          />
          <BlTextSubtitle3 text="Upload CSV" />
          <BlTextBody2
            text="Please ensure that the data is correct and that no users share the same email, including with the current members already registered in the team."
          />
          <div v-if="uploadLoading" class="h-35 grid place-items-center">
            <div class="blum-loader"></div>
          </div>
          <div
            v-else-if="!fileInput"
            class="w-full h-35 text-center flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-500"
            @dragenter.prevent="dropActive = true"
            @dragover.prevent="dropActive = true"
            @dragleave.prevent="dropActive = false"
            @drop.prevent="onDropUpload"
            :class="{ 'bg-slate-300': dropActive, 'bg-slate-100': !dropActive }"
          >
            <div
              class="h-10 w-10 flex items-center justify-center flex-shrink-0 bg-light-purple-100 rounded-full"
            >
              <BlIcon
                icon="download"
                class="h-6 w-6"
                :style="{ rotate: '180deg' }"
              />
            </div>
            <div class="flex items-center gap-1">
              <BlTextBody1 text="Drag and drop your files here or" />
              <div @click="openUploadDoc">
                <BlTextBody1
                  text="click to upload."
                  class="font-bold underline text-light-purple cursor-pointer"
                />
              </div>
            </div>
            <div>
              <BlTextBody1 text=" Each file must not exceed 25 MB." />
            </div>
          </div>
          <div v-else class="flex flex-col gap-2">
            <div
              class="py-2 px-2 flex items-center justify-between rounded"
              :class="{
                'bg-tomato-200': errors.length,
                'bg-grass-200': !errors.length
              }"
            >
              <div class="flex items-center gap-1">
                <BlTextSubtitle3
                  :text="fileInput.name"
                  class="max-w-64 truncate"
                  :class="{
                    'text-tomato-800': errors.length,
                    'text-grass-800': !errors.length
                  }"
                />
                <div
                  v-if="!errors.length"
                  class="h-0.5 w-0.5 rounded bg-grass-800"
                ></div>
                <BlTextBody2
                  v-if="!errors.length"
                  :text="`member(s) will be invited`"
                  class="text-grass-800"
                />
              </div>
              <div class="flex items-center gap-2">
                <BlIcon
                  :icon="errors.length ? 'close-circle' : 'check-circle'"
                  :color="errors.length ? 'tomato-7' : 'grass-6'"
                  class="h-5 w-5"
                />
                <BlIcon
                  icon="delete"
                  :color="errors.length ? 'tomato-7' : 'grass-6'"
                  clickable
                  class="h-5 w-5"
                  @clicked="fileInput = null"
                />
              </div>
            </div>

            <div v-if="errors.length">
              <div class="mb-2">
                <BlTextBody2 :text="errors.length" class="font-bold" />
                <BlTextBody2
                  text="member(s) have registered with the same email as the current member or with each other. Please remove the conflicting members and re-upload the CSV."
                />
              </div>
              <div class="max-h-128 overflow-y-auto">
                <div
                  v-for="(user, idx) in errors"
                  :key="idx"
                  class="flex items-center gap-2"
                >
                  <div class="h-0.5 w-0.5 bg-slate-800 rounded"></div>
                  <BlTextBody2 :text="`${user.email} - ${user.errors}`" />
                </div>
              </div>
            </div>

            <div v-if="!errors.length">
              <BlTextBody2
                text="By clicking 'Start to generate invitation', we will proceed with generating invitations for the members."
              />
            </div>
          </div>
        </div>
        <div class="grid grid-cols-2 lg:flex items-center justify-end gap-4">
          <BlInputButton
            type="plain"
            label="Cancel"
            @clicked="$emit('close-modal')"
          />
          <BlInputButton
            label="Start to generate invitation"
            :disabled="!Step1Verified"
            :loading="submitLoading"
            @clicked="onSubmit"
          />
        </div>
      </div>

      <div v-if="currentStep === 2" class="flex flex-col gap-4">
        <div class="py-4 px-6 flex flex-col items-center gap-4">
          <svg
            width="100"
            height="100"
            viewBox="0 0 100 100"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M45.9454 5.61916C47.941 2.85346 52.059 2.85346 54.0546 5.61916L59.0616 12.5581C60.3361 14.3244 62.6075 15.0624 64.6768 14.3826L72.8061 11.7119C76.0463 10.6474 79.3778 13.0679 79.3667 16.4784L79.3388 25.0351C79.3317 27.2133 80.7355 29.1454 82.8092 29.8117L90.9557 32.4294C94.2028 33.4727 95.4753 37.3891 93.4616 40.1418L88.4096 47.0479C87.1235 48.8059 87.1235 51.1941 88.4096 52.9521L93.4616 59.8582C95.4753 62.6109 94.2028 66.5273 90.9557 67.5706L82.8092 70.1883C80.7355 70.8546 79.3317 72.7867 79.3388 74.9649L79.3667 83.5216C79.3778 86.9321 76.0463 89.3526 72.8061 88.2881L64.6768 85.6174C62.6075 84.9376 60.3361 85.6756 59.0616 87.4419L54.0546 94.3808C52.059 97.1465 47.941 97.1465 45.9454 94.3808L40.9384 87.4419C39.6639 85.6756 37.3925 84.9376 35.3232 85.6174L27.1939 88.2881C23.9537 89.3526 20.6222 86.9321 20.6333 83.5216L20.6612 74.9649C20.6683 72.7867 19.2645 70.8546 17.1908 70.1883L9.04426 67.5706C5.79724 66.5273 4.52472 62.6109 6.53836 59.8582L11.5904 52.9521C12.8765 51.1941 12.8765 48.8059 11.5904 47.0479L6.53836 40.1418C4.52472 37.3891 5.79724 33.4727 9.04427 32.4294L17.1908 29.8117C19.2645 29.1454 20.6683 27.2133 20.6612 25.0351L20.6333 16.4784C20.6222 13.0679 23.9537 10.6474 27.1939 11.7119L35.3232 14.3826C37.3925 15.0624 39.6639 14.3244 40.9384 12.5581L45.9454 5.61916Z"
              fill="url(#paint0_linear_141_823)"
            />
            <path
              d="M73 51.9062C73 52.2792 72.8519 52.6368 72.5882 52.9006C72.3244 53.1643 71.9667 53.3125 71.5938 53.3125H68.7813V56.125C68.7813 56.4979 68.6331 56.8556 68.3694 57.1193C68.1057 57.383 67.748 57.5312 67.375 57.5312C67.0021 57.5312 66.6444 57.383 66.3807 57.1193C66.1169 56.8556 65.9688 56.4979 65.9688 56.125V53.3125H63.1563C62.7833 53.3125 62.4256 53.1643 62.1619 52.9006C61.8982 52.6368 61.75 52.2792 61.75 51.9062C61.75 51.5332 61.8982 51.1756 62.1619 50.9118C62.4256 50.6481 62.7833 50.5 63.1563 50.5H65.9688V47.6875C65.9688 47.3145 66.1169 46.9568 66.3807 46.6931C66.6444 46.4294 67.0021 46.2812 67.375 46.2812C67.748 46.2812 68.1057 46.4294 68.3694 46.6931C68.6331 46.9568 68.7813 47.3145 68.7813 47.6875V50.5H71.5938C71.9667 50.5 72.3244 50.6481 72.5882 50.9118C72.8519 51.1756 73 51.5332 73 51.9062ZM62.8276 62.2509C63.0677 62.5367 63.1844 62.9062 63.1521 63.278C63.1198 63.6499 62.9411 63.9936 62.6553 64.2337C62.3695 64.4738 62.0001 64.5906 61.6282 64.5583C61.2564 64.526 60.9126 64.3473 60.6725 64.0615C57.1358 59.8498 52.2737 57.5312 46.9844 57.5312C41.6951 57.5312 36.833 59.8498 33.2963 64.0615C33.0562 64.347 32.7125 64.5255 32.3408 64.5576C31.9692 64.5898 31.5999 64.473 31.3144 64.2329C31.0288 63.9928 30.8504 63.6491 30.8182 63.2774C30.7861 62.9057 30.9029 62.5365 31.143 62.2509C33.7692 59.1255 37.0352 56.9054 40.6651 55.7171C38.4606 54.3441 36.7633 52.2905 35.8299 49.8669C34.8965 47.4434 34.7779 44.7817 35.4919 42.2847C36.2059 39.7877 37.7138 37.5912 39.7873 36.0275C41.8609 34.4638 44.3873 33.6179 46.9844 33.6179C49.5815 33.6179 52.1079 34.4638 54.1815 36.0275C56.255 37.5912 57.7629 39.7877 58.4769 42.2847C59.1909 44.7817 59.0723 47.4434 58.1389 49.8669C57.2055 52.2905 55.5082 54.3441 53.3037 55.7171C56.9336 56.9054 60.1996 59.1255 62.8276 62.2509ZM46.9844 54.7187C48.7923 54.7187 50.5595 54.1826 52.0627 53.1782C53.5658 52.1738 54.7374 50.7463 55.4292 49.076C56.1211 47.4058 56.3021 45.5679 55.9494 43.7948C55.5967 42.0217 54.7261 40.393 53.4478 39.1147C52.1695 37.8363 50.5408 36.9658 48.7677 36.6131C46.9945 36.2604 45.1567 36.4414 43.4864 37.1332C41.8162 37.8251 40.3886 38.9966 39.3843 40.4998C38.3799 42.003 37.8438 43.7702 37.8438 45.5781C37.8466 48.0015 38.8105 50.3248 40.5241 52.0384C42.2377 53.752 44.561 54.7159 46.9844 54.7187Z"
              fill="white"
            />
            <defs>
              <linearGradient
                id="paint0_linear_141_823"
                x1="50"
                y1="0"
                x2="50"
                y2="100"
                gradientUnits="userSpaceOnUse"
              >
                <stop stop-color="#F3F0F7" />
                <stop offset="1" stop-color="#4A178F" />
              </linearGradient>
            </defs>
          </svg>
          <div v-if="jobDetails" class="text-center">
            <BlTextSubtitle3
              :text="
                `Importing ${jobDetails.total_success}/${totalInvited} member(s)`
              "
            />
          </div>
          <div class="relative h-2 bg-light-purple-100 rounded">
            <div
              class="absolute z-10 left-0 h-2 bg-light-purple rounded transition-all duration-300"
              :style="{
                width: `${(jobDetails.total_success * 100) / totalInvited}%`
              }"
            ></div>
          </div>
          <div class="text-center">
            <BlTextBody2
              v-if="!processCompleted"
              :text="
                `Don't close this window/tab until the process is completed. You can continue to work from other tabs.`
              "
            />
            <BlTextBody2
              v-if="processCompleted"
              :text="
                `The process has been completed. You can continue to work from other tabs.`
              "
            />
          </div>
        </div>
        <div class="grid grid-cols-2 lg:flex items-center justify-end gap-4">
          <BlInputButton
            type="plain"
            label="Cancel"
            :loading="cancelLoading"
            @clicked="onCancelJob"
          />
          <BlInputButton
            label="Close and download invitation"
            :disabled="!processCompleted"
            :loading="downloadLoading"
            @clicked="onDownloadDetails"
          />
        </div>
      </div>
    </div>
  </BlBackdrop>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import BlBackdrop from "../../../BlBackdrop.vue";
import BlTextSubtitle2 from "../../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../../BlTextSubtitle3.vue";
import BlTextBody1 from "../../../BlTextBody1.vue";
import BlTextBody2 from "../../../BlTextBody2.vue";
import BlTextBody3 from "../../../BlTextBody3.vue";
import BlInputText from "../../../BlInputText.vue";
import BlInputRadio from "../../../BlInputRadio.vue";
import BlSelectBox from "../../../BlSelectBox.vue";
import BlInputButton from "../../../BlInputButton.vue";
import BlIcon from "../../../BlIcon.vue";

export default {
  name: "InviteCSVBulkUserModal",
  components: {
    BlBackdrop,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody1,
    BlTextBody2,
    BlTextBody3,
    BlInputText,
    BlInputRadio,
    BlSelectBox,
    BlInputButton,
    BlIcon
  },
  props: {
    centerId: { type: Number, required: true }
    // @close-modal
  },
  data() {
    return {
      submitLoading: false,
      currentStep: 1,
      invitationProgress: [
        { step: 1, label: "Upload CSV" },
        { step: 2, label: "Finalize and create invitation" }
      ],
      //
      uploadLoading: false,
      dropActive: false,
      fileInput: null,
      errors: [],
      jobId: "",
      jobDetails: null,
      processCompleted: false,
      cancelLoading: false,
      downloadLoading: false
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    totalInvited() {
      let count = 0;
      if (!this.jobDetails) return count;
      count += this.jobDetails.total_success;
      count += this.jobDetails.total_errors;
      count += this.jobDetails.remaining_users;
      return count;
    },
    Step1Verified() {
      return this.fileInput !== null && !this.errors.length;
    },
    Step2Verified() {
      return this.processCompleted;
    }
  },
  watch: {},
  mounted() {
    const events = ["dragenter", "dragover", "dragleave", "drop"];
    events.forEach(eventName => {
      document.body.addEventListener(eventName, this.preventDefaults);
    });
  },
  destroyed() {
    const events = ["dragenter", "dragover", "dragleave", "drop"];
    events.forEach(eventName => {
      document.body.removeEventListener(eventName, this.preventDefaults);
    });
  },
  methods: {
    ...mapActions([
      "getCenter",
      "checkCSVBulkUser",
      "inviteCSVBulkUser",
      "checkInviteUserJob",
      "cancelInviteUserJob",
      "downloadInviteUserJobDetails",
      "showToast"
    ]),
    preventDefaults(e) {
      e.preventDefault();
    },
    openUploadDoc() {
      const input = document.getElementById("upload_csv_member");
      if (input) input.click();
    },
    onDropUpload(e) {
      this.dropActive = false;
      const files = e.dataTransfer.files;
      if (files && files.length) {
        this.onUploadFile(files[0]);
      } else {
        this.fileInput = null;
      }
    },
    onButtonUpload(e) {
      const files = e.target.files;
      if (files && files.length) {
        this.onUploadFile(files[0]);
      } else {
        this.fileInput = null;
      }
    },
    async onUploadFile(file) {
      const maxFileSize = 26214400;
      const exceedLimit = file.size > maxFileSize;
      if (exceedLimit) return;

      const formData = new FormData();
      formData.append(`center_id`, this.centerId.toString());
      formData.append(`file`, file);

      this.uploadLoading = true;
      const { success, data } = await this.checkCSVBulkUser(formData);
      this.fileInput = file;
      this.uploadLoading = false;
      if (!success) {
        this.errors = data;
        return;
      }
      this.errors = [];
    },
    async onSubmit() {
      const formData = new FormData();
      formData.append(`center_id`, this.centerId.toString());
      formData.append(`invite_type`, "csv");
      formData.append(`file`, this.fileInput);

      this.submitLoading = true;
      const { success, data } = await this.inviteCSVBulkUser(formData);
      if (!success) {
        this.submitLoading = false;
        return;
      }

      this.jobId = data.job_id;
      this.currentStep = 2;
      this.processCompleted = false;
      setTimeout(() => {
        this.onCheckJob(data.job_id);
        this.submitLoading = false;
      }, 1000);
    },
    async onCheckJob(id) {
      const params = { id };
      const { success, data } = await this.checkInviteUserJob(params);
      if (!success) return;
      this.jobDetails = data;

      if (data.status === "in_progress" && data.remaining_users !== 0) {
        setTimeout(() => {
          this.onCheckJob(id);
        }, 2000);
      } else {
        this.processCompleted = true;
      }
    },
    async onCancelJob() {
      if (this.processCompleted) {
        this.getCenter({ id: this.centerId });
        this.$emit("after-commit");
        this.$emit("close-modal");
        return;
      }

      const params = {
        id: this.jobId
      };
      this.cancelLoading = true;
      const { success } = await this.cancelInviteUserJob(params);
      this.cancelLoading = false;
      if (!success) return;

      this.getCenter({ id: this.centerId });
      this.$emit("after-commit");
      this.$emit("close-modal");
    },
    //
    async onDownloadDetails() {
      const params = {
        id: this.jobId
      };

      this.downloadLoading = true;
      const { response, success } = await this.downloadInviteUserJobDetails(
        params
      );
      this.downloadLoading = false;
      if (!success) return;

      const title = "Downloading details";
      this.showToast({ title, message: "", type: "success" });

      const href = URL.createObjectURL(response.data);
      const link = document.createElement("a");

      let filename = "";
      const disposition = response.headers["content-disposition"];
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(disposition);
      if (matches != null && matches[1]) {
        filename = matches[1].replace(/['"]/g, "");
      }

      link.href = href;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(href);

      this.getCenter({ id: this.centerId });
      this.$emit("after-commit");
      this.$emit("close-modal");
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/superAdmin/center/centerTeam/InviteCSVBulkUserModal.vue